import { Controller } from "stimulus";
import { SupercastLockOnPlayEvent } from "utils/events";

import { maybeConnectBridge } from "utils/hotwire";

/** 🚨🚨🚨🚨 WARNING: This controller has a bridge component counterpart. 🚨🚨🚨🚨
 * 
 * This means that the functionality provided below is handled natively in the mobile apps.
 * Ensure that any changes to this controller are also reflected in the bridge component counterpart, and
 * tested in both Android and iOS.
 * 
 * You can find the bridge component counterpart in:
 * engines/subscriber_v2/app/components/bridge/play_button_controller.js
 */

export default class PlayButtonController extends Controller {
  connect() {
    this.playbackDataPromise = null;
    this.locked = !this.hasPlayTarget;
    maybeConnectBridge(this.element);
    this.listenForTimeUpdates();
    this.boundLockPlay = this.lockPlay.bind(this);
    window.addEventListener("supercast-lock-play", this.boundLockPlay);
    this.resolvePlaybackData();

    if (this.autoResolveValue) {
      this.autoResolveTimeout = setTimeout(() => {
        this.openResolution();
      }, 1000);
    }
  }

  disconnect() {
    if (this.autoResolveTimeout) {
      clearTimeout(this.autoResolveTimeout);
    }

    if (this.boundTimeUpdate) {
      window.removeEventListener("supercast-timeupdate", this.boundTimeUpdate);
    }

    if (this.boundLockPlay) {
      window.removeEventListener("supercast-lock-play", this.boundLockPlay);
    }
  }

  listenForTimeUpdates() {
    this.boundTimeUpdate = this.timeUpdate.bind(this);
    window.addEventListener("supercast-timeupdate", this.boundTimeUpdate);
  }

  timeUpdate(event) {
    if (event.detail.itemId == this.itemIdValue && event.detail.itemType == this.itemTypeValue) {
      if (event.detail.isPlaying) {
        this.updatePlayButton("playing");
      } else {
        this.updatePlayButton("paused");
      }
    } else if (this.locked) {
      this.updatePlayButton("locked");
    } else {
      this.updatePlayButton("paused");
    }
  }

  updatePlayButton(state) {
    // Hide all targets
    if (this.hasPlayTarget) { this.playTarget.hidden = true; }
    if (this.hasPauseTarget) { this.pauseTarget.hidden = true; }
    if (this.hasLockedTarget) { this.lockedTarget.hidden = true; }
    if (this.hasLoadingTarget) { this.loadingTarget.hidden = true; }

    if (state === "playing") {
      if (this.hasPauseTarget) { this.pauseTarget.hidden = false; }
    } else if (state === "paused") {
      if (this.hasPlayTarget) { this.playTarget.hidden = false; }
    } else if (state === "loading") {
      if (this.hasLoadingTarget) { this.loadingTarget.hidden = false; }
    } else if (state === "locked") {
      if (this.hasLockedTarget) { this.lockedTarget.hidden = false; }
    } else {
      console.error("Unknown play button state:", state);
    }
  }

  async play(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    return this.playNow();
  }

  async playNow() {
    this.updatePlayButton("loading");

    // Use requestAnimationFrame to ensure that the play button is updated immediately
    window.requestAnimationFrame(async () => {
      await this.resolvePlaybackData();
      if (this.locked) {
        return;
      }

      if (!this.hasAudioUrlValue || !this.audioUrlValue) {
        if (this.element.tagName === "A" && this.element.href) {
          window.location.href = this.element.href;
        }
        return;
      }

      const audioPlayer = this.application.getControllerForElementAndIdentifier(
        document.querySelector('[data-audio-player]'),
        'media--audio-player'
      );
      audioPlayer.loadEpisode({
        itemId: this.itemIdValue,
        itemType: this.itemTypeValue,
        audioUrl: this.audioUrlValue,
        timestamp: this.timestampValue,
        mediaMetadata: {
          title: this.nameValue,
          artist: this.channelNameValue,
          artworkUrl: this.artworkUrlValue
        }
      });

      window.queueClient
        .dequeueIfHead({ itemId: this.itemIdValue, itemType: this.itemTypeValue })
        .catch(console.error);

      if (this.lockOnPlayValue) {
        const event = new SupercastLockOnPlayEvent({ itemId: this.itemIdValue, itemType: this.itemTypeValue });
        window.dispatchEvent(event);
      }
    });
  }

  lockPlay(event) {
    if (event.detail.itemId != this.itemIdValue || event.detail.itemType != this.itemTypeValue) {
      if (this.hasLockedTarget) { this.lockedTarget.hidden = false; }
      if (this.hasPlayTarget) { this.playTarget.hidden = true; }
      if (this.hasPauseTarget) { this.pauseTarget.hidden = false; }

      this.locked = true;
    }
  }

  openResolution(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (!this.hasResolutionUrlValue || !this.resolutionUrlValue) {
      return;
    }

    const url = new URL(this.resolutionUrlValue, window.location.origin);
    url.searchParams.set("modal", "true");
    if (typeof window.renderModal === "function") {
      window.renderModal(url.toString());
    } else {
      window.location.href = url.toString();
    }
  }

  pause(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    const audioPlayer = this.application.getControllerForElementAndIdentifier(
      document.querySelector('[data-audio-player]'),
      'media--audio-player'
    );
    audioPlayer.pause();
    this.updatePlayButton("paused");
  }

  extractFeedItemId() {
    const source = this.element.getAttribute("href") || "";
    const match = source.match(/\/subscriber_v2\/feed_items\/(\d+)/);
    return match ? match[1] : null;
  }

  extractTimestamp() {
    if (this.hasTimestampValue) {
      return this.timestampValue;
    }

    const href = this.element.getAttribute("href");
    if (!href) {
      return 0;
    }

    try {
      const url = new URL(href, window.location.origin);
      const value = parseInt(url.searchParams.get("timestamp"), 10);
      return Number.isFinite(value) ? value : 0;
    } catch (_error) {
      return 0;
    }
  }

  async resolvePlaybackData() {
    this.timestampValue = this.extractTimestamp();

    if ((this.hasAudioUrlValue && this.audioUrlValue) && (this.hasItemIdValue && this.itemIdValue)) {
      if (!this.hasItemTypeValue || !this.itemTypeValue) {
        this.itemTypeValue = "Episode";
      }
      return true;
    }

    if (this.playbackDataPromise) {
      return this.playbackDataPromise;
    }

    const feedItemId = this.extractFeedItemId();
    if (!feedItemId) {
      return false;
    }

    this.playbackDataPromise = fetch(`/subscriber_v2/feed_items/${feedItemId}.json`, {
      headers: { Accept: "application/json" },
      credentials: "include"
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (!data) {
          return false;
        }

        if (data.locked || data.can_play === false) {
          this.locked = true;
          this.resolutionUrlValue = data.resolution_url;
          this.setActionToOpenResolution();
          this.markLockedMention();
          return false;
        }

        this.itemIdValue = `${data.item_id || feedItemId}`;
        this.itemTypeValue = data.item_type || "Episode";
        if (data.audio_url) {
          this.audioUrlValue = data.audio_url;
        }

        return this.hasAudioUrlValue && !!this.audioUrlValue;
      })
      .catch(() => false)
      .finally(() => {
        this.playbackDataPromise = null;
      });

    return this.playbackDataPromise;
  }

  markLockedMention() {
    if (this.element.tagName !== "A") {
      return;
    }

    const text = this.element.textContent || "";
    if (!text.trim().startsWith("🔒")) {
      this.element.textContent = `🔒 ${text}`;
    }
  }

  setActionToOpenResolution() {
    const currentAction = this.element.getAttribute("data-action") || "";
    if (currentAction.includes("click->play-button#openResolution")) {
      return;
    }

    const actions = currentAction.trim().split(/\s+/).filter(Boolean);
    let replacedPlayAction = false;

    const normalizedActions = actions.map((action) => {
      if (action === "click->play-button#play" || action === "play-button#play") {
        replacedPlayAction = true;
        return "click->play-button#openResolution";
      }

      return action;
    });

    if (!replacedPlayAction) {
      normalizedActions.push("click->play-button#openResolution");
    }

    this.element.setAttribute("data-action", [...new Set(normalizedActions)].join(" "));
  }
}

PlayButtonController.targets = ['play', 'pause', 'locked', 'loading'];
PlayButtonController.values = {
  itemId: String,
  itemType: String,
  audioUrl: String,
  timestamp: Number,
  lockOnPlay: Boolean,
  resolutionUrl: String,
  autoResolve: Boolean,
  name: String,
  channelName: String,
  artworkUrl: String
};